// Blog entries come from the build-time trimmed JSON, NOT contentlayer:
// this module is imported by the sitewide header (a client component), and
// importing allPosts here shipped the full ~7MB corpus (compiled MDX
// bodies) in every page's client bundle. blog-search-items.json is emitted
// by scripts/build-blog-dates.mjs after `npm run contentlayer` and carries
// only the five fields search needs (~120KB raw).
import blogSearchItems from './blog-search-items.json'
import { worksData } from '@/data/works'
import { servicesData } from '@/data/services'

export interface SearchableItem {
  id: string
  title: string
  description: string
  category: 'blog' | 'project' | 'service' | 'page'
  url: string
  tags?: string[]
}

export function buildSearchIndex(): SearchableItem[] {
  const items: SearchableItem[] = []

  // Index blog posts (pre-trimmed at build time — see import note above)
  items.push(...(blogSearchItems as SearchableItem[]))

  // Index projects
  worksData.forEach(work => {
    items.push({
      id: `project-${work.slug}`,
      title: work.title,
      description: work.description,
      category: 'project',
      url: work.slug,
      tags: work.technologies || [],
    })
  })

  // Index services
  Object.entries(servicesData).forEach(([slug, service]) => {
    items.push({
      id: `service-${slug}`,
      title: service.title,
      description: service.description,
      category: 'service',
      url: `/services/${slug}`,
      tags: [],
    })
  })

  // Partner delivery is a distinct agency channel, not a fifth direct-client
  // entry in servicesData, whose slugs all resolve beneath /services.
  items.push({
    id: 'service-agency-partner-delivery',
    title: 'Consulting',
    description:
      'White-label website, application, and AI visibility delivery for agencies and consultancies.',
    category: 'service',
    url: '/consulting',
    tags: [
      'white-label',
      'agency partner',
      'agency consulting',
      'consulting',
      'website delivery',
      'AI visibility',
    ],
  })

  // Index static pages.
  //
  // These are the destinations a visitor searches for by name, and until now
  // only three of them existed here. A query for "pricing" could not return
  // /pricing at any rank because the page was not a document in the index at
  // all — it lost to blog posts that merely mention the word. Every core
  // destination reachable from the header now has a record, with the title
  // and description taken from the page's own metadata so the two cannot
  // drift into describing different things.
  const staticPages: SearchableItem[] = [
    {
      id: 'page-home',
      title: 'Home',
      description: 'AI-native design agency transforming ideas into revenue',
      category: 'page',
      url: '/',
    },
    {
      id: 'page-pricing',
      title: 'Pricing',
      description:
        'AI products you own (from $35K), growth services ($4,500 sprint), visibility audits (free). Performance-based pricing.',
      category: 'page',
      url: '/pricing',
      tags: ['pricing', 'price', 'cost', 'plans', 'rates', 'how much'],
    },
    {
      id: 'page-platform',
      title: 'Radar by Pixelmojo',
      description:
        'Radar runs 13 AI visibility tools on your domain. Start with a free check, then unlock the full audit.',
      category: 'page',
      url: '/platform',
      tags: ['radar', 'AI visibility', 'audit', 'free check', 'GEO', 'AEO'],
    },
    {
      id: 'page-services',
      title: 'Services',
      description:
        'Done for you, owned by you. Scoped up front, shipped in checkpoints.',
      category: 'page',
      url: '/services',
      tags: ['services', 'retainer', 'sprint', 'studio'],
    },
    {
      id: 'page-projects',
      title: 'Projects',
      description:
        'Real AI products that generate measurable ROI. From enterprise logistics systems to marketing automation platforms.',
      category: 'page',
      url: '/projects',
      tags: ['projects', 'portfolio', 'case studies', 'work'],
    },
    {
      id: 'page-blogs',
      title: 'Blog',
      description:
        'Learn how we build AI products that generate revenue. Deep dives on product design, AI implementation, and growth strategies.',
      category: 'page',
      url: '/blogs',
      tags: ['blog', 'field notes', 'articles', 'writing'],
    },
    {
      id: 'page-tools',
      title: 'Free AI Visibility Tools',
      description:
        'Free tools to test and optimize your website for AI search engines. Check bot access, structured data, llms.txt, and more.',
      category: 'page',
      url: '/tools',
      tags: ['tools', 'free tools', 'checker', 'audit tools'],
    },
    {
      id: 'page-methodology',
      title: 'Radar Methodology',
      description:
        'How the Radar AI visibility score is calculated, category by category.',
      category: 'page',
      url: '/platform/methodology',
      tags: ['methodology', 'scoring', 'how it is scored', 'grade'],
    },
    {
      id: 'page-about',
      title: 'About',
      description: 'Learn about our team and mission',
      category: 'page',
      url: '/about',
    },
    {
      id: 'page-contact',
      title: 'Contact',
      description: 'Get in touch with us',
      category: 'page',
      url: '/contact-us',
    },
  ]

  items.push(...staticPages)
  return items
}

export function getCategoryLabel(category: SearchableItem['category']): string {
  const labels = {
    blog: 'Blog',
    project: 'Project',
    service: 'Service',
    page: 'Page',
  }
  return labels[category]
}
